import { CustomForm, FORM_ITEM_TYPES } from '../types/CustomForm'
import { AnswerState } from './dynamicFormReducer'
import { toSnakeCase } from './stringUtils'

/** Legacy payload format: { form: { <sectionKey>: [{ id: itemId, value: answer }] } } */
export interface SubmissionPayload {
  form: Record<string, Array<{ id: string; value: unknown }>>
}

/** New payload format: { form: { <sectionKey>: { [itemId]: answer } } } */
export interface SubmissionPayloadV2 {
  form: Record<string, Record<string, unknown>>
}

/**
 * 🔄 isNumericType: Determines if a form item type is numeric
 * @param typeId - The form item type ID
 * @returns true if the type expects numeric values
 */
const isNumericType = (typeId: number): boolean => {
  return typeId === FORM_ITEM_TYPES.NUMBER_FIELD
}

/**
 * 🔄 castNumericValue: Safely casts a value to number for numeric fields
 * @param value - The raw value from form answers
 * @param typeId - The form item type ID
 * @returns Properly cast value (number, null, or original value)
 */
const castNumericValue = (value: unknown, typeId: number): unknown => {
  // Non-numeric types pass through unchanged (preserving empty strings, etc.)
  if (!isNumericType(typeId)) {
    return value
  }

  // Handle empty string case for numeric fields
  if (value === '' || value === null || value === undefined) {
    console.log('🔄 castNumericValue: Empty numeric value, returning 0', {
      value,
      typeId,
    })
    // https://sportninja.atlassian.net/browse/SN-6584
    return null
  }

  // Convert string numbers to actual numbers
  if (typeof value === 'string' && value.trim() !== '') {
    const numericValue = Number(value)
    if (!isNaN(numericValue)) {
      console.log('🔄 castNumericValue: String converted to number', {
        original: value,
        converted: numericValue,
        typeId,
      })
      return numericValue
    }
  }

  // If already a number, return as-is
  if (typeof value === 'number') {
    console.log('🔄 castNumericValue: Already a number, passing through', {
      value,
      typeId,
    })
    return value
  }

  // Fallback for invalid numeric values
  console.log('🔄 castNumericValue: Invalid numeric value, returning 0', {
    value,
    typeId,
  })
  return 0
}

/**
 * 🔄 mapFormAnswers: Maps form answers to the array-per-section payload format, including all values
 * @param form - The CustomForm definition with groups and items
 * @param answers - The AnswerState containing user responses
 * @returns SubmissionPayload - The array-per-section payload structure for backend submission (including empty values)
 */
export const mapFormAnswers = (
  form: CustomForm,
  answers: AnswerState
): SubmissionPayload => {
  console.log(
    '🔄 mapFormAnswers: Mapping form answers to array-per-section payload (including all values)',
    {
      formId: form.id,
      answersCount: Object.keys(answers).length,
    }
  )

  const nested: Record<string, Array<{ id: string; value: unknown }>> = {}

  // Iterate through every item in every group
  form?.groups
    ?.filter((group) => group.is_enabled)
    .forEach((group) => {
      const sectionKey = toSnakeCase(group.name) // e.g. "Personal Info" → "personal_info"

      group?.items
        ?.filter((item) => item.is_enabled)
        ?.forEach((item) => {
          const userAnswer = answers[item.id]
          const typeId = item.form_item_type.id

          // Normalize answers: provide default values for empty fields
          const hasNoUserAnswer =
            userAnswer === undefined || userAnswer === null
          let normalisedAnswer: unknown

          if (hasNoUserAnswer) {
            // Provide appropriate default values for empty fields
            switch (typeId) {
              case FORM_ITEM_TYPES.CHECKBOX:
                normalisedAnswer = false
                break
              case FORM_ITEM_TYPES.NUMBER_FIELD:
                normalisedAnswer = null
                break
              default:
                normalisedAnswer = ''
                break
            }
          } else {
            normalisedAnswer = userAnswer
          }

          // Cast the value if needed (primarily for numeric fields)
          const castedValue = castNumericValue(normalisedAnswer, typeId)

          // Initialize section array if it doesn't exist
          if (!nested[sectionKey]) {
            nested[sectionKey] = []
          }

          // Include the non-empty value in the array payload
          nested[sectionKey].push({ id: item.id, value: castedValue })

          console.log('🔄 mapFormAnswers: Included item in array payload', {
            itemId: item.id,
            itemType: typeId,
            sectionKey,
            isNumeric: isNumericType(typeId),
            userAnswer: normalisedAnswer,
            finalValue: castedValue,
            finalType: typeof castedValue,
          })
        })
    })

  console.log(
    '🔄 mapFormAnswers: Array-per-section payload created (including all values)',
    {
      sectionCount: Object.keys(nested).length,
      nested,
    }
  )

  return { form: nested }
}

/**
 * 🔄 stripRelationshipItems: Returns a copy of the form with all RELATIONSHIP
 * items removed from every group. Used to keep the relationship field out of the
 * submission payload for existing players (it is only relevant when creating a
 * new player). Mirrors the same filter applied to the rendered admin form.
 * @param form - The CustomForm definition with groups and items
 * @returns A new CustomForm without relationship items (falsy input passes through)
 */
export const stripRelationshipItems = (form: CustomForm): CustomForm => {
  if (!form?.groups) {
    return form
  }

  return {
    ...form,
    groups: form.groups.map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => item.form_item_type.id !== FORM_ITEM_TYPES.RELATIONSHIP
      ),
    })),
  }
}

/**
 * 🔄 mapFormAnswersObject: Maps form answers to the key-value payload format, including all values
 * @param form - The CustomForm definition with groups and items
 * @param answers - The AnswerState containing user responses
 * @returns SubmissionPayloadV2 - The key-value payload structure for backend submission (including empty values)
 */
export const mapFormAnswersObject = (
  form: CustomForm,
  answers: AnswerState
): SubmissionPayloadV2 => {
  console.log(
    '🔄 mapFormAnswersObject: Mapping form answers to key-value payload (including all values)',
    {
      formId: form.id,
      answersCount: Object.keys(answers).length,
    }
  )

  const nested: Record<string, Record<string, unknown>> = {}

  // Iterate through every item in every group
  form?.groups
    ?.filter((group) => group.is_enabled)
    ?.forEach((group) => {
      const sectionKey = toSnakeCase(group.name) // e.g. "Personal Info" → "personal_info"

      group?.items
        ?.filter((item) => item.is_enabled)
        ?.forEach((item) => {
          const userAnswer = answers[item.id]
          const typeId = item.form_item_type.id

          // Normalize answers: provide default values for empty fields
          const hasNoUserAnswer =
            userAnswer === undefined || userAnswer === null
          let normalisedAnswer: unknown

          if (hasNoUserAnswer) {
            // Provide appropriate default values for empty fields
            switch (typeId) {
              case FORM_ITEM_TYPES.CHECKBOX:
                normalisedAnswer = false
                break
              case FORM_ITEM_TYPES.NUMBER_FIELD:
                // https://sportninja.atlassian.net/browse/SN-6584
                normalisedAnswer = null
                break
              default:
                normalisedAnswer = ''
                break
            }
          } else {
            normalisedAnswer = userAnswer
          }

          // Cast the value if needed (primarily for numeric fields)
          const castedValue = castNumericValue(normalisedAnswer, typeId)

          // Initialize section object if it doesn't exist
          if (!nested[sectionKey]) {
            nested[sectionKey] = {}
          }

          // Include the non-empty value in the key-value payload
          nested[sectionKey][item.id] = castedValue

          console.log(
            '🔄 mapFormAnswersObject: Included item in key-value payload',
            {
              itemId: item.id,
              itemType: typeId,
              sectionKey,
              isNumeric: isNumericType(typeId),
              userAnswer: normalisedAnswer,
              finalValue: castedValue,
              finalType: typeof castedValue,
            }
          )
        })
    })

  console.log(
    '🔄 mapFormAnswersObject: Key-value payload created (including all values)',
    {
      sectionCount: Object.keys(nested).length,
      nested,
    }
  )

  return { form: nested }
}
